import cx from "classnames";
import type { ReactNode } from "react";
import { t } from "ttag";

import { Box } from "metabase/ui";

import ViewSidebarS from "./ViewSidebar.module.css";

interface ViewSidebarProps {
  side: "left" | "right";
  width?: number;
  isOpen?: boolean;
  children?: ReactNode;
  "aria-label"?: string;
}

// Both sides can be mounted at once (e.g. filters open on the left,
// summarize open on the right), so each needs its own accessible name —
// otherwise they're two identical, unlabelled <aside> landmarks (WCAG 1.3.1,
// aria_landmark_name_unique). Callers with more specific content (e.g. "Filters")
// can override via the aria-label prop.
const DEFAULT_LABEL = {
  left: () => t`Left sidebar`,
  right: () => t`Right sidebar`,
};

export const ViewSidebar = ({
  side = "right",
  width = 400,
  isOpen,
  children,
  "aria-label": ariaLabel,
}: ViewSidebarProps) => (
  // If we passed `width` as prop, it would end up in the final HTML elements.
  // This would ruin the animation, so we pass it as `widthProp`.
  <Box
    className={cx(ViewSidebarS.ViewSidebarAside, {
      [ViewSidebarS.rightSide]: side === "right",
      [ViewSidebarS.leftSide]: side === "left",
      [ViewSidebarS.isOpen]: isOpen,
    })}
    component="aside"
    aria-label={ariaLabel ?? DEFAULT_LABEL[side]()}
    data-testid={`sidebar-${side}`}
    w={isOpen ? width : undefined}
    left={side === "left" ? 0 : undefined}
    right={side === "right" ? 0 : undefined}
  >
    <Box w={width} pos="absolute" h="100%">
      {children}
    </Box>
  </Box>
);
